/**
 * useCollapseStates Hook
 *
 * Manages collapse/expand states for UI panels and sections.
 * Extracted from AppContent.js as part of Phase 1 refactoring.
 *
 * Features:
 * - Device state panel collapse
 * - Credential grid collapse
 * - Last action panel collapse
 * - Device actions panel collapse
 * - All states persist via UIStore
 *
 * @returns {Object} Collapse states and toggle functions
 */

import { useUIStore } from "../stores";
import logger from "../utils/logger";

const COLLAPSE_STORAGE_KEY = (deviceId) => `rw_collapse_${deviceId}`;

const saveCollapseState = (deviceId, key, value) => {
  if (!deviceId) return;
  try {
    const existing = JSON.parse(localStorage.getItem(COLLAPSE_STORAGE_KEY(deviceId)) || '{}');
    localStorage.setItem(COLLAPSE_STORAGE_KEY(deviceId), JSON.stringify({ ...existing, [key]: value }));
  } catch (e) {}
};

export const useCollapseStates = ({ selectedDeviceId } = {}) => {
  // Get collapse states from UIStore (persisted)
  const {
    isDeviceStateCollapsed,
    toggleDeviceStateCollapsed,
    isCredentialGridCollapsed,
    toggleCredentialGridCollapsed,
    isLastActionCollapsed,
    toggleLastActionCollapsed,
    isDeviceActionsCollapsed,
    toggleDeviceActionsCollapsed,
    isDeviceSettingsCollapsed,
    toggleDeviceSettingsCollapsed,
  } = useUIStore();

  // Enhanced toggle functions with logging
  const toggleDeviceState = () => {
    const next = !isDeviceStateCollapsed;
    toggleDeviceStateCollapsed();
    saveCollapseState(selectedDeviceId, 'deviceState', next);
    logger.log(`[useCollapseStates] Device state ${next ? "collapsed" : "expanded"}`);
  };

  const toggleCredentialGrid = () => {
    const next = !isCredentialGridCollapsed;
    toggleCredentialGridCollapsed();
    saveCollapseState(selectedDeviceId, 'credentialGrid', next);
    logger.log(`[useCollapseStates] Credential grid ${next ? "collapsed" : "expanded"}`);
  };

  const toggleLastAction = () => {
    const next = !isLastActionCollapsed;
    toggleLastActionCollapsed();
    saveCollapseState(selectedDeviceId, 'lastAction', next);
    logger.log(`[useCollapseStates] Last action ${next ? "collapsed" : "expanded"}`);
  };

  const toggleDeviceActions = () => {
    const next = !isDeviceActionsCollapsed;
    toggleDeviceActionsCollapsed();
    saveCollapseState(selectedDeviceId, 'deviceActions', next);
    logger.log(`[useCollapseStates] Device actions ${next ? "collapsed" : "expanded"}`);
  };

  const toggleDeviceSettings = () => {
    const next = !isDeviceSettingsCollapsed;
    toggleDeviceSettingsCollapsed();
    saveCollapseState(selectedDeviceId, 'deviceSettings', next);
    logger.log(`[useCollapseStates] Device settings ${next ? "collapsed" : "expanded"}`);
  };

  return {
    // Device State Panel
    isDeviceStateCollapsed,
    toggleDeviceState,
    toggleDeviceStateCollapsed, // Keep original for backward compatibility

    // Credential Grid
    isCredentialGridCollapsed,
    toggleCredentialGrid,
    toggleCredentialGridCollapsed, // Keep original for backward compatibility

    // Last Action Panel
    isLastActionCollapsed,
    toggleLastAction,
    toggleLastActionCollapsed, // Keep original for backward compatibility

    // Device Actions Panel
    isDeviceActionsCollapsed,
    toggleDeviceActions,
    toggleDeviceActionsCollapsed, // Keep original for backward compatibility

    // Device Settings Panel
    isDeviceSettingsCollapsed,
    toggleDeviceSettings,
    toggleDeviceSettingsCollapsed,
  };
};
